import { Fragment } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import BreadcrumbJsonLd from "@/components/feature/BreadcrumbJsonLd";

/**
 * パンくずリストの共通コンポーネント。
 * 可視パンくずと BreadcrumbList 構造化データ（JSON-LD）を、
 * 同じ items から描画して階層・名称・URLを常に一致させる。
 *
 * 正式ルール:
 *  - HOME → "/" へのリンク
 *  - 中間階層（親カテゴリ） → 実在する親ページへのリンク（href を指定）
 *  - 現在ページ → 非リンク（href を指定しない）
 *  - 実在しない階層（例: TREATMENT）は使用しない
 *
 * デザインは従来のパンくずと同一（フォント・文字サイズ・文字色・矢印・gap・
 * 上下余白・flex-wrap）に保ち、階層と実装方式のみを共通化している。
 */
export type BreadcrumbItem = {
  /** 表示ラベル */
  label: string;
  /** リンク先。指定した場合のみリンク表示（現在ページは指定しない） */
  href?: string;
  /** JSON-LD（BreadcrumbList）用のパス。現在ページは自身のパスを指定 */
  path: string;
};

export default function Breadcrumb({ items }: { items: BreadcrumbItem[] }) {
  return (
    <>
      <BreadcrumbJsonLd items={items.map((item) => ({ name: item.label, path: item.path }))} />
      <Reveal>
        <nav
          aria-label="パンくずリスト"
          className="flex flex-wrap items-center gap-2 text-[11px] tracking-wide text-foreground-500"
        >
          {items.map((item, index) => (
            <Fragment key={`${item.label}-${index}`}>
              {index > 0 ? <i className="ri-arrow-right-s-line text-sm" aria-hidden /> : null}
              {item.href ? (
                <Link to={item.href} className="transition-colors hover:text-foreground-800">
                  {item.label}
                </Link>
              ) : (
                <span className="text-foreground-800">{item.label}</span>
              )}
            </Fragment>
          ))}
        </nav>
      </Reveal>
    </>
  );
}